import {
	ContentAction,
	ContentBodyFormat,
	ContentPlacement,
	ContentScopeType,
	ContentTranslationOrigin,
	Role,
} from '@today/types'
import { z } from 'zod'

// Content (TO-419) request schemas. Wire fields are snake_case (the ticket's
// contract); each schema transforms to the camelCase input the service consumes.
// `tenantId` is never part of an input — it comes from the DB-resolved principal
// (ADR-0034). Every object is `.strict()` so unknown properties are refused.
// Fields accepted only to be refused as immutable are marked below; the service
// maps their presence to `CONTENT_IMMUTABLE_FIELD`.

/** Content type code: lowercase snake (`legal`, `marketing`). */
export const contentTypeCodeSchema = z
	.string()
	.regex(/^[a-z][a-z0-9_]{1,63}$/, 'type code must be 2–64 chars of a-z 0-9 _ starting with a letter')

/** Text code: uppercase snake (`PRIVACY_GENERAL`). */
export const contentTextCodeSchema = z
	.string()
	.regex(/^[A-Z][A-Z0-9_]{1,63}$/, 'text code must be 2–64 chars of A-Z 0-9 _ starting with a letter')

/** BCP-47 language-REGION (`es-ES`, `gl-ES`). */
export const contentLocaleSchema = z.string().regex(/^[a-z]{2,3}-[A-Z]{2}$/, 'locale must look like es-ES')

/** The platform default source locale for new texts. */
export const CONTENT_DEFAULT_SOURCE_LOCALE = 'es-ES'

export const CONTENT_BODY_MAX_LENGTH = 100_000

const nameSchema = z.string().trim().min(1).max(200)
const descriptionSchema = z.string().max(2000)
const titleSchema = z.string().trim().min(1).max(300)
const bodySchema = z.string().min(1).max(CONTENT_BODY_MAX_LENGTH)
const timestampSchema = z.iso.datetime({ offset: true }).transform((value) => new Date(value))
const uniqueList = <T>(values: T[]): boolean => new Set(values).size === values.length

const approverRolesSchema = z.array(z.enum(Role)).max(5).refine(uniqueList, 'approver_roles must be distinct')
const requiredLocalesSchema = z
	.array(contentLocaleSchema)
	.max(20)
	.refine(uniqueList, 'required_locales must be distinct')

// ─── Types ────────────────────────────────────────────────────────────────────

export const contentTypeCreateSchema = z
	.object({
		code: contentTypeCodeSchema,
		name: nameSchema,
		description: descriptionSchema.nullable().optional(),
		requires_approval: z.boolean().default(true),
		approver_roles: approverRolesSchema.default([]),
		forbid_self_approval: z.boolean().default(true),
		required_locales: requiredLocalesSchema.default([]),
		allow_locale_fallback: z.boolean().default(false),
	})
	.strict()
	.refine((v) => !v.requires_approval || v.approver_roles.length > 0, {
		message: 'approver_roles needs 1–5 roles when requires_approval is true',
	})
	.transform((v) => ({
		code: v.code,
		name: v.name,
		description: v.description ?? null,
		requiresApproval: v.requires_approval,
		approverRoles: v.approver_roles,
		forbidSelfApproval: v.forbid_self_approval,
		requiredLocales: v.required_locales,
		allowLocaleFallback: v.allow_locale_fallback,
	}))
export type ContentTypeCreateInput = z.infer<typeof contentTypeCreateSchema>

export const contentTypeUpdateSchema = z
	.object({
		// Accepted only so a change is refused as CONTENT_IMMUTABLE_FIELD.
		code: z.unknown().optional(),
		is_system: z.unknown().optional(),
		name: nameSchema.optional(),
		description: descriptionSchema.nullable().optional(),
		requires_approval: z.boolean().optional(),
		approver_roles: approverRolesSchema.optional(),
		forbid_self_approval: z.boolean().optional(),
		required_locales: requiredLocalesSchema.optional(),
		allow_locale_fallback: z.boolean().optional(),
	})
	.strict()
	.transform((v) => ({
		immutableFields: (['code', 'is_system'] as const).filter((key) => v[key] !== undefined),
		name: v.name,
		description: v.description,
		requiresApproval: v.requires_approval,
		approverRoles: v.approver_roles,
		forbidSelfApproval: v.forbid_self_approval,
		requiredLocales: v.required_locales,
		allowLocaleFallback: v.allow_locale_fallback,
	}))
export type ContentTypeUpdateInput = z.infer<typeof contentTypeUpdateSchema>

// ─── Texts ────────────────────────────────────────────────────────────────────

export const contentTextCreateSchema = z
	.object({
		type_code: contentTypeCodeSchema,
		code: contentTextCodeSchema,
		name: nameSchema,
		description: descriptionSchema.nullable().optional(),
		// Omitted → the tenant default locale, resolved by content-service (content-operations REQ-007).
		source_locale: contentLocaleSchema.optional(),
		external_id: z.string().min(1).max(128).nullable().optional(),
		title: titleSchema,
		body: bodySchema,
		body_format: z.enum(ContentBodyFormat).default(ContentBodyFormat.MARKDOWN),
	})
	.strict()
	.transform((v) => ({
		typeCode: v.type_code,
		code: v.code,
		name: v.name,
		description: v.description ?? null,
		sourceLocale: v.source_locale,
		externalId: v.external_id ?? null,
		title: v.title,
		body: v.body,
		bodyFormat: v.body_format,
	}))
export type ContentTextCreateInput = z.infer<typeof contentTextCreateSchema>

export const contentTextUpdateSchema = z
	.object({
		// Accepted only so a change is refused as CONTENT_IMMUTABLE_FIELD.
		code: z.unknown().optional(),
		type_code: z.unknown().optional(),
		source_locale: z.unknown().optional(),
		external_id: z.unknown().optional(),
		name: nameSchema.optional(),
		description: descriptionSchema.nullable().optional(),
	})
	.strict()
	.transform((v) => ({
		immutableFields: (['code', 'type_code', 'source_locale', 'external_id'] as const).filter(
			(key) => v[key] !== undefined,
		),
		name: v.name,
		description: v.description,
	}))
export type ContentTextUpdateInput = z.infer<typeof contentTextUpdateSchema>

// ─── Versions & localizations ─────────────────────────────────────────────────

export const contentVersionCreateSchema = z
	.object({
		based_on_version_id: z.uuid().optional(),
		change_summary: z.string().max(2000).nullable().optional(),
	})
	.strict()
	.transform((v) => ({ basedOnVersionId: v.based_on_version_id, changeSummary: v.change_summary ?? null }))
export type ContentVersionCreateInput = z.infer<typeof contentVersionCreateSchema>

/** Origins a caller may declare when adding a language (`original` is reserved for v1's source draft). */
const addableOrigins = [ContentTranslationOrigin.MANUAL, ContentTranslationOrigin.IMPORT, ContentTranslationOrigin.AI]

export const contentLocalizationCreateSchema = z
	.object({
		locale: contentLocaleSchema,
		title: titleSchema,
		body: bodySchema,
		body_format: z.enum(ContentBodyFormat).default(ContentBodyFormat.MARKDOWN),
		translation_origin: z.enum(addableOrigins).default(ContentTranslationOrigin.MANUAL),
		translated_from_id: z.uuid().nullable().optional(),
	})
	.strict()
	.transform((v) => ({
		locale: v.locale,
		title: v.title,
		body: v.body,
		bodyFormat: v.body_format,
		translationOrigin: v.translation_origin as ContentTranslationOrigin,
		translatedFromId: v.translated_from_id ?? null,
	}))
export type ContentLocalizationCreateInput = z.infer<typeof contentLocalizationCreateSchema>

export const contentLocalizationUpdateSchema = z
	.object({
		title: titleSchema,
		body: bodySchema,
		body_format: z.enum(ContentBodyFormat),
	})
	.strict()
	.transform((v) => ({ title: v.title, body: v.body, bodyFormat: v.body_format }))
export type ContentLocalizationUpdateInput = z.infer<typeof contentLocalizationUpdateSchema>

export const contentActionSchema = z
	.object({
		action: z.enum(ContentAction),
		reason: z.string().max(2000).nullable().optional(),
		valid_from: timestampSchema.optional(),
		valid_to: timestampSchema.nullable().optional(),
	})
	.strict()
	.transform((v) => ({
		action: v.action,
		reason: v.reason ?? null,
		validFrom: v.valid_from,
		validTo: v.valid_to,
	}))
export type ContentActionInput = z.infer<typeof contentActionSchema>

// ─── Assignments & resolution ─────────────────────────────────────────────────

export const contentAssignmentCreateSchema = z
	.object({
		text_id: z.uuid(),
		scope_type: z.enum(ContentScopeType),
		scope_id: z.uuid().nullable().optional(),
		placement: z.enum(ContentPlacement).optional(),
		replaces_text_id: z.uuid().nullable().optional(),
		sort_order: z.number().int().min(0).max(10_000).default(0),
	})
	.strict()
	.transform((v) => ({
		textId: v.text_id,
		scopeType: v.scope_type,
		scopeId: v.scope_id ?? null,
		placement: v.placement,
		replacesTextId: v.replaces_text_id ?? null,
		sortOrder: v.sort_order,
	}))
export type ContentAssignmentCreateInput = z.infer<typeof contentAssignmentCreateSchema>

export const contentAssignmentUpdateSchema = z
	.object({
		// Accepted only so a change is refused as CONTENT_IMMUTABLE_FIELD.
		text_id: z.unknown().optional(),
		scope_type: z.unknown().optional(),
		scope_id: z.unknown().optional(),
		placement: z.enum(ContentPlacement).optional(),
		replaces_text_id: z.uuid().nullable().optional(),
		sort_order: z.number().int().min(0).max(10_000).optional(),
	})
	.strict()
	.transform((v) => ({
		immutableFields: (['text_id', 'scope_type', 'scope_id'] as const).filter((key) => v[key] !== undefined),
		placement: v.placement,
		replacesTextId: v.replaces_text_id,
		sortOrder: v.sort_order,
	}))
export type ContentAssignmentUpdateInput = z.infer<typeof contentAssignmentUpdateSchema>

export const contentResolveSchema = z
	.object({
		product_ids: z.array(z.uuid()).min(1).max(50).refine(uniqueList, 'product_ids must be distinct'),
		locale: contentLocaleSchema,
		at: timestampSchema.optional(),
	})
	.strict()
	.transform((v) => ({ productIds: v.product_ids, locale: v.locale, at: v.at }))
export type ContentResolveInput = z.infer<typeof contentResolveSchema>

/**
 * `POST /texts/query` body (TO-630, AD-19): the large-selector escape hatch for
 * the live delivery read. Exactly one of `codes`/`ids` (both or neither is a
 * `422`), plus the required locale. Mirrors `GET /texts/live`.
 */
export const contentTextsQuerySchema = z
	.object({
		codes: z.array(contentTextCodeSchema).min(1).max(200).optional(),
		ids: z.array(z.uuid()).min(1).max(200).optional(),
		locale: contentLocaleSchema,
	})
	.strict()
	.refine((v) => (v.codes === undefined) !== (v.ids === undefined), 'provide exactly one of codes or ids')
export type ContentTextsQueryInput = z.infer<typeof contentTextsQuerySchema>

/** `page` ≥ 1 (default 1), `limit` 1–100 (default 20). */
export const contentPageQuerySchema = z.object({
	page: z.coerce.number().int().min(1).default(1),
	limit: z.coerce.number().int().min(1).max(100).default(20),
})
